import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MeetingRoom from '../../components/VideoCall/MeetingRoom';
import { communicationsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useAppConfig } from '../../contexts/AppConfigContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const MeetingJoin = () => {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showError } = useNotification();
  const { appName } = useAppConfig();
  const [joinData, setJoinData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!meetingId) {
        setError('Missing meeting ID');
        setLoading(false);
        return;
      }
      try {
        const res = await communicationsAPI.joinMeeting(meetingId);
        const data = res.data?.data || res.data;
        const meetingAccess = data?.meetingAccess || data?.jitsi;
        if (!meetingAccess?.domain || !meetingAccess?.roomName) {
          throw new Error('Invalid join response');
        }
        if (!cancelled) {
          setJoinData(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Failed to join meeting');
          showError(err.response?.data?.message || 'Failed to join meeting');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [meetingId, showError]);

  const handleLeave = () => {
    navigate('/meetings');
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center min-h-[50vh">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !joinData) {
    return (
      <div className="page-container">
        <div className="p-4 bg-red-50 text-red-700 rounded-lg">
          {error || 'Unable to load meeting'}
        </div>
        <button
          type="button"
          onClick={() => navigate('/meetings')}
          className="mt-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
        >
          Back to Meetings
        </button>
      </div>
    );
  }

  // Only teachers and admins can end meeting for all; students and parents cannot (no button shown)
  const canEndMeetingForAll = user?.role === 'teacher' || user?.role === 'admin';

  return (
    <div className="jitsi-meet-page" style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <MeetingRoom
        initialMeetingAccess={joinData.meetingAccess || joinData.jitsi}
        meetingTitle={joinData.meeting?.title}
        meetingSubject={joinData.meeting?.subject}
        meetingTeacherName={joinData.meeting?.teacherName}
        appName={appName}
        onLeave={handleLeave}
        isTeacher={canEndMeetingForAll}
      />
    </div>
  );
};

export default MeetingJoin;
